import React from 'react'
import { BarProps } from 'recharts'
import { SpendGraphData, SpendType } from 'src/components/SpendGraph/SpendGraph'

export interface CustomBarShapeProps extends BarProps {
  barRadius: number
  gap: number
  payload: SpendGraphData
  currency: string
  type: SpendType
}

export const customSpendBarShape = (
  props: CustomBarShapeProps,
): React.ReactElement => {
  const {
    x,
    y,
    width,
    height,
    fill,
    fillOpacity,
    payload,
    barRadius,
    currency,
    type,
    gap,
  } = props

  let isLast = false
  let yAdjusted = y ?? 0

  switch (type) {
    case SpendType.physical: {
      if (!hasPhysicalSpend(payload, currency)) {
        return <g></g>
      }
      isLast =
        !hasVirtualSpend(payload, currency) &&
        !hasBillPaySpend(payload, currency)
      break
    }
    case SpendType.billPay: {
      if (!hasBillPaySpend(payload, currency)) {
        return <g></g>
      }
      const isFirst = !hasPhysicalSpend(payload, currency)
      isLast = !hasVirtualSpend(payload, currency)

      if (!isFirst) {
        yAdjusted = +yAdjusted - gap
      }
      break
    }
    case SpendType.virtual: {
      if (!hasVirtualSpend(payload, currency)) {
        return <g></g>
      }
      const isFirst =
        !hasBillPaySpend(payload, currency) &&
        !hasPhysicalSpend(payload, currency)
      isLast = true

      if (!isFirst) {
        if (
          hasBillPaySpend(payload, currency) &&
          hasPhysicalSpend(payload, currency)
        ) {
          yAdjusted = +yAdjusted - 2 * gap
        } else {
          yAdjusted = +yAdjusted - gap
        }
      }
      break
    }
    default:
      break
  }

  const pathD = getRoundedRectPath(
    +(x ?? 0),
    +yAdjusted,
    +(width ?? 0),
    +(height ?? 0),
    isLast,
    barRadius,
  )

  return (
    <g>
      <path d={pathD} fill={fill} fillOpacity={fillOpacity} />
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill="transparent"
        style={{ pointerEvents: 'all' }}
      />
    </g>
  )
}

const hasPhysicalSpend = (
  payload: SpendGraphData,
  currency: string,
): boolean => {
  if (currency === 'cad') {
    return payload.cadPhysical !== undefined && payload.cadPhysical !== 0
  } else {
    return payload.usdPhysical !== undefined && payload.usdPhysical !== 0
  }
}

const hasBillPaySpend = (
  payload: SpendGraphData,
  currency: string,
): boolean => {
  if (currency === 'cad') {
    return payload.cadBillPay !== undefined && payload.cadBillPay !== 0
  } else {
    return payload.usdBillPay !== undefined && payload.usdBillPay !== 0
  }
}

const hasVirtualSpend = (
  payload: SpendGraphData,
  currency: string,
): boolean => {
  if (currency === 'cad') {
    return payload.cad !== undefined && payload.cad !== 0
  } else {
    return payload.usd !== undefined && payload.usd !== 0
  }
}

const getRoundedRectPath = (
  x: number,
  y: number,
  width: number,
  height: number,
  isLast: boolean,
  radius: number,
): string => {
  const topLeft = isLast ? radius : 0
  const topRight = isLast ? radius : 0
  const bottomRight = 0
  const bottomLeft = 0

  return `
    M${x + topLeft},${y}
    H${x + width - topRight}
    ${topRight > 0 ? `A${topRight},${topRight} 0 0 1 ${x + width},${y + topRight}` : ''}
    V${y + height - bottomRight}
    ${bottomRight > 0 ? `A${bottomRight},${bottomRight} 0 0 1 ${x + width - bottomRight},${y + height}` : ''}
    H${x + bottomLeft}
    ${bottomLeft > 0 ? `A${bottomLeft},${bottomLeft} 0 0 1 ${x},${y + height - bottomLeft}` : ''}
    V${y + topLeft}
    ${topLeft > 0 ? `A${topLeft},${topLeft} 0 0 1 ${x + topLeft},${y}` : ''}
    Z
  `.trim()
}
